import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { adminApi, assetUrl, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { RichEditor } from "@/admin/RichEditor";
import { ImageField, SliderField, ColorField } from "@/admin/fields";

const EMPTY = { name: "", group: "", image: "", image_width: 100, image_bg: "", content: "" };

export const ListingDialog = ({ open, listing, groups, onClose, onSaved }) => {
  const defaultBg = useSite().content.school_page.image_bg;
  const [form, setForm] = useState(EMPTY);
  const [newGroup, setNewGroup] = useState("");
  const [extraGroups, setExtraGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm(listing ? { ...EMPTY, ...listing } : EMPTY);
      setNewGroup("");
    }
  }, [open, listing]);

  const allGroups = [...new Set([...groups.map((g) => g.name), ...extraGroups])].sort((a, b) => a.localeCompare(b));

  const addGroup = () => {
    const g = newGroup.trim();
    if (!g) return;
    setExtraGroups((x) => [...x, g]);
    set("group")(g);
    setNewGroup("");
  };

  const save = async () => {
    if (!form.name.trim() || !form.group) return toast.error("Name and group are required");
    setBusy(true);
    try {
      const body = { name: form.name, group: form.group, image: form.image, image_width: form.image_width, image_bg: form.image_bg, content: form.content };
      if (listing) await adminApi.put(`/admin/schools/${listing.id}`, body);
      else await adminApi.post("/admin/schools", body);
      toast.success("Listing saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto font-arial-all" data-testid="listing-dialog">
        <DialogHeader><DialogTitle>{listing ? "Edit listing" : "New listing"}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => set("name")(e.target.value)} data-testid="listing-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Group</Label>
            <Select value={form.group} onValueChange={set("group")}>
              <SelectTrigger data-testid="listing-group-select"><SelectValue placeholder="Select a group" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {allGroups.map((g) => <SelectItem key={g} value={g} data-testid={`listing-group-option-${g}`}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2 pt-1">
              <Input value={newGroup} onChange={(e) => setNewGroup(e.target.value)} placeholder="…or add a new group" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addGroup())} data-testid="listing-new-group-input" />
              <Button type="button" variant="outline" onClick={addGroup} data-testid="listing-new-group-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Image</Label>
            <ImageField value={form.image} onChange={set("image")} testId="listing-image" />
          </div>
          <div className="space-y-1.5">
            <Label>Image size on page</Label>
            <SliderField value={form.image_width} onChange={set("image_width")} min={10} max={100} step={5} suffix="%" testId="listing-image-width" />
            {form.image && (
              <div className="rounded-lg border bg-ivory p-3" data-testid="listing-image-live-preview">
                <div className="mx-auto p-[4%]" style={{ width: `${form.image_width}%`, background: form.image_bg || defaultBg }}>
                  <img src={assetUrl(form.image)} alt="" className="block w-full h-auto" />
                </div>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Image box colour</Label>
            <p className="text-xs text-muted-foreground">Leave empty to use the default from Website Content → School pages ({defaultBg}).</p>
            <ColorField value={form.image_bg || ""} onChange={set("image_bg")} testId="listing-image-bg" />
            {form.image_bg && <button type="button" onClick={() => set("image_bg")("")} className="text-xs text-brand underline" data-testid="listing-image-bg-reset">Use default colour</button>}
          </div>
          <div className="space-y-1.5">
            <Label>Content</Label>
            <RichEditor value={form.content} onChange={set("content")} testId="listing-content-editor" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="listing-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark" data-testid="listing-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
